import { ICustomerDisplayProvider } from '../ICustomerDisplayProvider';
import { ICustomerDisplayDevice } from '../ICustomerDisplayDevice';
import { ICustomerDisplaySessionDelegate } from '../ICustomerDisplaySessionDelegate';
import { Subject } from 'rxjs';
import { Injector } from '@angular/core';
import { SavedDeviceInfo, UniversalCustomerDisplayDevice } from './UniversalCustomerDisplayDevice';
import { IDiscovery } from './IDiscovery';
import { NetworkDiscovery } from './network/NetworkDiscovery';
import { InAppDiscovery } from './inapp/InAppDiscovery';
import { UniversalCustomerDisplaySession } from './UniversalCustomerDisplaySession';
import { ICustomerDisplaySession } from '@core/services/system/customer-display/ICustomerDisplaySession';
import { Platform } from '@ionic/angular';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { WebDemoDiscovery } from './web-demo/WebDemoDiscovery';
import { PlatformService } from '@core/services/system/platform/platform.service';
import moment from 'moment';
import { LogService } from '@core/services/system/logger/log.service';
import { CustomerDisplayReporting } from '@core/services/system/customer-display/CustomerDisplayReporting';
import { ErrorLevel, ILogger, ILoggerFactory } from '@paymash/logging';
import { ignorePromiseError } from '@core/services/utils/promise.utils';
import { TimerService, Interval } from '@core/services/system/timer/timer.service';

type ReConnectAttemptSetup = [moment.DurationInputArg1, moment.DurationInputArg2];

export class UniversalCustomerDisplayProvider extends ICustomerDisplayProvider {
  private readonly logService: LogService;
  private readonly logger: ILogger;
  private delegate: ICustomerDisplaySessionDelegate;

  public readonly providerId = 'UniversalCustomerDisplayProvider';

  private readonly devices_: UniversalCustomerDisplayDevice<unknown>[] = [];
  private readonly devicesSubject = new Subject<ICustomerDisplayDevice[]>();

  private readonly discoveries: IDiscovery<UniversalCustomerDisplayDevice<unknown>, unknown>[];

  private readonly platformService: PlatformService;
  private readonly platform: Platform;
  private readonly timerService: TimerService;
  private readonly localStorage: LocalStorage;

  constructor(injector: Injector, private readonly report: CustomerDisplayReporting) {
    super();

    this.platformService = injector.get(PlatformService);
    this.platform = injector.get(Platform);
    this.timerService = injector.get(TimerService);
    this.localStorage = injector.get(LocalStorage);
    this.logService = injector.get(LogService);
    this.logger = this.logService.createLogger('UniversalCustomerDisplayProvider');

    this.discoveries = this.getDiscoveries(this.logService);

    this.platform.resume.subscribe(() => {
      this.connectToOfflineDevices().then();
    });
  }

  private getDiscoveries(loggerFactory: ILoggerFactory): IDiscovery<UniversalCustomerDisplayDevice<unknown>, unknown>[] {
    const logger = this.logger.child();
    logger.updateParams({ method: 'getDiscoveries' });
    logger.debug('Build discoveries list');

    if (this.platformService.isWeb) {
      logger.debug('Web platform, return WebDemoDiscovery');
      return [new WebDemoDiscovery(this, loggerFactory)];
    }

    const discoveries = [];

    if (this.platformService.isAndroid) {
      logger.debug('Add InAppDiscovery');
      discoveries.push(new InAppDiscovery(this, loggerFactory));
    }

    if (this.platformService.isNativePlatform) {
      this.logger.debug('Add NetworkDiscovery');
      discoveries.push(new NetworkDiscovery(this, this.platform, loggerFactory));
    }

    return discoveries;
  }

  async initialize(delegate: ICustomerDisplaySessionDelegate) {
    this.delegate = delegate;

    this.discoveries.forEach((d) => d.devices.subscribe((devices) => this.onDevicesDiscovered(d.discoveryId, devices)));

    this.logger.info('Universal customer display provider initialized');
  }

  private turnedOn: boolean;

  async turnOn() {
    if (this.turnedOn) {
      return;
    }
    this.turnedOn = true;

    await this.restoreConnectedDevices();

    this.startAutoConnectTimer();

    this.logger.info('Provider turned on');
  }

  async turnOff() {
    if (!this.turnedOn) {
      return;
    }
    this.turnedOn = false;

    this.stopAutoConnectTimer();

    this.logger.info('Provider turned off');
  }

  get devices() {
    return this.devicesSubject;
  }

  async connect(device: ICustomerDisplayDevice) {
    const logger = this.logger.child();
    logger.updateParams({ device });

    if (!(device.status === 'disconnected' || device.status === 'offline')) {
      return;
    }

    const device_ = device as UniversalCustomerDisplayDevice<unknown>;

    try {
      device_.status = 'offline';
      this.updateDevices();
      await this.saveConnectedDevices();

      device_.status = 'connecting';
      this.updateDevices();

      const driver = device_.createDriver();
      await driver.connect();

      device_.status = 'online';

      device_.failedConnectionAttempts = undefined;
      logger.info('Device connected, set online status and restore "failedConnectionAttempts" field');

      this.updateDevices();

      const session = new UniversalCustomerDisplaySession(device_, driver, this.logService);
      session.closed.subscribe(this.onSessionClosed.bind(this));

      await this.delegate.onDeviceConnected(session);
    } catch (e) {
      logger.error(e, 'Cannot connect to device', { level: ErrorLevel.Low }, { device });

      device_.status = 'offline';

      if (device_.failedConnectionAttempts) {
        device_.failedConnectionAttempts.last = new Date();
        device_.failedConnectionAttempts.count++;
      } else {
        device_.failedConnectionAttempts = { last: new Date(), count: 0 };
      }

      this.updateDevices();
    }
  }

  async disconnect(device: ICustomerDisplayDevice, session: ICustomerDisplaySession | undefined) {
    const logger = this.logger.child();
    logger.updateParams({ device });

    if (session) {
      const session_ = session as UniversalCustomerDisplaySession;
      await session_.driver.disconnect().catch(ignorePromiseError);
    }

    const device_ = device as UniversalCustomerDisplayDevice<unknown>;
    device_.status = 'disconnected';
    if (device_.autoConnect) {
      device_.autoConnect = false;
    }
    this.updateDevices();

    await this.saveConnectedDevices();

    await this.delegate.onDeviceDisconnected(device, session);
    logger.info('Disconnected from device');
  }

  private isDeviceDiscoveryStarted: boolean;

  startEagerDiscoveries() {
    // Eager discoveries (the built-in in-app display) are idempotent via their
    // own internal guard, so this is safe to call alongside startDeviceDiscovery().
    this.discoveries.filter((d) => d.eager).forEach((d) => d.startDiscovery());
    this.logger.debug('Eager device discoveries started');
  }

  startDeviceDiscovery() {
    if (this.isDeviceDiscoveryStarted) return;
    this.isDeviceDiscoveryStarted = true;
    this.discoveries.forEach((d) => d.startDiscovery());
    this.logger.debug('Device discovery started');
  }

  stopDeviceDiscovery() {
    if (!this.isDeviceDiscoveryStarted) return;
    this.isDeviceDiscoveryStarted = false;
    this.discoveries.forEach((d) => d.stopDiscovery());
    this.logger.debug('Device discovery stopped');
  }

  private updateDevices() {
    this.devicesSubject.next([...this.devices_]);
  }

  private onDevicesDiscovered(discoveryId: string, devices: UniversalCustomerDisplayDevice<unknown>[]) {
    devices.forEach((device) => {
      const device_ = this.devices_.find((d) => d.equals(device));
      if (!device_) {
        this.devices_.push(device);
        this.onDeviceAdded(device);
      } else {
        device_.loadFrom(device);
        device_.discovered = true;
        this.onDeviceUpdated(device_);
      }
    });

    const notFoundDevices = this.devices_
      .filter((device) => device.discoveryId === discoveryId)
      .filter((device) => !devices.find((d) => d.equals(device)));

    const devicesToRemove: UniversalCustomerDisplayDevice<unknown>[] = [];

    notFoundDevices.forEach((device_) => {
      device_.discovered = false;
      if (device_.status === 'disconnected' || device_.temporary) {
        devicesToRemove.push(device_);
      }
    });

    devicesToRemove.forEach((device) => {
      this.devices_.splice(this.devices_.indexOf(device), 1);
      this.onDeviceRemoved(device);
    });

    this.updateDevices();
  }

  private onDeviceAdded(device: UniversalCustomerDisplayDevice<unknown>) {
    this.logger.debug('Device added', { device });
    this.report.deviceAdded(device);
    if (device.autoConnect && device.status === 'disconnected') {
      this.logger.debug('Auto connect option is "true" and device status is "disconnected", connect to the device immediately', { device });
      this.connect(device).then();
    }
  }

  private onDeviceUpdated(device: UniversalCustomerDisplayDevice<unknown>) {
    if (device.status === 'offline') {
      this.connect(device).then();
    }
  }

  private onDeviceRemoved(device: UniversalCustomerDisplayDevice<unknown>) {
    this.report.deviceRemoved(device);
  }

  private async saveConnectedDevices() {
    const devices = this.devices_.filter((d) => d.status !== 'disconnected' || d.autoConnect !== undefined);

    const saveDevicesInfo: SavedDeviceInfo[] = [];
    for (const device of devices) {
      const discovery = this.findDiscoveryById(device.discoveryId);
      saveDevicesInfo.push(discovery.getDeviceInfo(device));
    }

    this.localStorage.set('UniversalCustomerDisplayProvider.SavedDevices', JSON.stringify(saveDevicesInfo));
  }

  private async restoreConnectedDevices() {
    this.logger.debug('Restore connected devices');

    const savedDevicesString = this.localStorage.get('UniversalCustomerDisplayProvider.SavedDevices');
    this.logger.debug('Received "savedDevicesString" from localStorage', { savedDevicesString });
    if (!savedDevicesString) {
      this.logger.debug('"savedDevicesString" undefined, skipping restore connected devices');
      return;
    }

    const savedDevicesInfo: SavedDeviceInfo[] = JSON.parse(savedDevicesString);
    for (const deviceInfo of savedDevicesInfo) {
      const discovery = this.findDiscoveryById(deviceInfo.discoveryId);
      if (!discovery) continue;
      const device = discovery.restoreDeviceFromInfo(deviceInfo);
      const device_ = this.devices_.find((d) => d.equals(device));
      if (!device_) {
        this.devices_.push(device);
      } else {
        device_.status = device.status;
        device_.autoConnect = device.autoConnect;
      }
      this.updateDevices();
    }
  }

  private async connectToOfflineDevices(options?: { connectEvenNotDiscovered?: boolean }) {
    const devices = this.devices_;

    // this.logger.debug('Connect to offline devices', { options, devices });

    const offlineDevices = devices.filter((d) => d.status === 'offline');
    if (offlineDevices.length < 1) return;

    for (const device of offlineDevices) {
      if (device.status !== 'offline') continue;

      if (!device.discovered && !options?.connectEvenNotDiscovered) {
        continue;
      }

      if (!this.isReadyToReconnect(device)) {
        continue;
      }

      await this.connect(device);
    }
  }

  private isReadyToReconnect(device: UniversalCustomerDisplayDevice<unknown>): boolean {
    const failedAttempts = device.failedConnectionAttempts;

    if (!failedAttempts) {
      return true;
    }

    const attemptsMap: { retriesCount: number; interval: ReConnectAttemptSetup }[] = [
      { retriesCount: 0, interval: [0, 'second'] },
      { retriesCount: 1, interval: [10, 'seconds'] },
      { retriesCount: 10, interval: [30, 'seconds'] },
      { retriesCount: 20, interval: [1, 'minutes'] },
    ];

    const findAttempt = (retriesCount: number) => {
      for (let attempt of attemptsMap.reverse()) {
        if (attempt.retriesCount <= retriesCount) return attempt.interval;
      }
      return attemptsMap[attemptsMap.length - 1];
    };

    const failedAttemptsCount = failedAttempts.count;
    const attempt = findAttempt(failedAttemptsCount);

    const delay = moment.duration(attempt[0], attempt[1]);

    return moment().isSameOrAfter(moment(failedAttempts.last).add(delay));
  }

  private findDiscoveryById(discoveryId: string): IDiscovery<UniversalCustomerDisplayDevice<unknown>, unknown> | undefined {
    return this.discoveries.find((d) => d.discoveryId === discoveryId);
  }

  private async onSessionClosed(session: UniversalCustomerDisplaySession) {
    this.logger.info('Session closed', { device: session.device });

    const device_ = this.devices_.find((d) => d.equals(session.device));
    if (device_) {
      this.logger.debug('Device found in the list, set "offline" state', { device: device_ });
      device_.status = 'offline';
      this.updateDevices();
    } else {
      this.logger.debug('Device not found in the list', { device: session.device });
    }

    await this.delegate.onDeviceDisconnected(device_, session);
  }

  //region AutoConnectTimer
  private autoConnectTimerId: Interval | undefined;

  private startAutoConnectTimer() {
    this.stopAutoConnectTimer();
    const intervalSec = 10;
    this.autoConnectTimerId = this.timerService.setInterval(
      () => this.connectToOfflineDevices({ connectEvenNotDiscovered: true }),
      1000 * intervalSec
    );
    this.logger.debug('Auto connect timer started');
  }

  private stopAutoConnectTimer() {
    if (this.autoConnectTimerId === undefined) {
      return;
    }

    this.timerService.clearInterval(this.autoConnectTimerId);
    this.autoConnectTimerId = undefined;
    this.logger.debug('Auto connect timer stopped');
  }
  //endregion
}
